import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import NotreHistoire from './components/NotreHistoire';
import NosValeurs from './components/NosValeurs';
import Equipe from './components/Equipe';
import Certifications from './components/Certifications';
import ZonesDesservies from './components/ZonesDesservies';
import NosChiffres from './components/NosChiffres';
import Garantie from './components/Garantie';
import Temoignage from './components/Temoignage';
import CTAFinal from './components/CTAFinal';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'apropos-ld') el.remove();
  });
}

export default function AProposPage() {
  usePageSEO({
    title: "À propos | Couvreur-Lévis.com | Plateforme de mise en relation",
    description: "Couvreur-Lévis.com connecte les propriétaires de Lévis avec les meilleurs couvreurs RBQ certifiés. Découvrez notre processus de sélection.",
    urlPath: "/a-propos",
    keywords: "couvreur Lévis, plateforme couvreur, toiture Lévis, CCQ, soumission",
    ogImage: '/og/og-a-propos.jpg',
    ogImageAlt: 'Notre plateforme Couvreur-Lévis.com - Couvreurs partenaires RBQ à Lévis',
    jsonLdSchema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
        { '@type': 'ListItem', position: 2, name: 'À propos', item: 'https://couvreur-levis.com/a-propos' },
      ],
    },
  });

  useEffect(() => {
    document.title = "À propos | Couvreur-Lévis.com | Plateforme de mise en relation";
    setMeta('description', "Couvreur-Lévis.com connecte les propriétaires de Lévis avec les meilleurs couvreurs RBQ certifiés. Découvrez notre processus de sélection.");
    setMeta('keywords', 'couvreur Lévis, plateforme couvreur, toiture Lévis, CCQ, soumission');
    setMeta('geo.position', '46.8085;-71.1879');
    setMeta('geo.placename', 'Lévis, QC');
    setMeta('geo.region', 'CA-QC');

    removeOldLdJson();

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://couvreur-levis.com/#organization",
          name: "Couvreur-Lévis.com",
          url: "https://couvreur-levis.com",
          logo: "https://couvreur-levis.com/og-default.jpg",
          description: "Plateforme de mise en relation avec des couvreurs certifiés à Lévis.",
          telephone: "+1-418-476-9950",
          email: "contact@couvreur-levis.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Lévis",
            addressRegion: "QC",
            addressCountry: "CA",
          },
          sameAs: [],
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://couvreur-levis.com/#localbusiness",
          name: "Couvreur-Lévis.com",
          image: "https://couvreur-levis.com/og-default.jpg",
          url: "https://couvreur-levis.com/a-propos",
          telephone: "+1-418-476-9950",
          email: "contact@couvreur-levis.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Lévis",
            addressRegion: "QC",
            addressCountry: "CA",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 46.8085,
            longitude: -71.1879,
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:00",
              closes: "17:00",
            },
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Saturday"],
              opens: "09:00",
              closes: "14:00",
            },
          ],
          priceRange: "$$",
          areaServed: {
            "@type": "Place",
            name: "Lévis et environs",
          },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'apropos-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <NotreHistoire />
        <NosValeurs />
        <Equipe />
        <Certifications />
        <ZonesDesservies />
        <NosChiffres />
        <Garantie />
        <Temoignage />
        <CTAFinal />
      </main>
      <Footer />
    </div>
  );
}